import { lazy, useCallback, useMemo, useState } from "react";
import { useLanguage } from "../../../../../stores/LanguageStore";
import {
  Button,
  CloseButton,
  Group,
  LoadingOverlay,
  Paper,
  Stack,
  Stepper,
} from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import StepMapping from "./StepMapping";
import MappingStats from "./MappingStats";

import system_map from "./default_system_fields.json";
import StepImportSettings from "./StepImportSettings";
import { createSMTP } from "../../../../../api/EmailAPI";
import { useNavigate } from "react-router-dom";
import { defaultSMTPConfigs } from "../../../tools/emails/components/defaultConfigs";
import lazyRetry from "../../../../../functions/lazyRetry";

const StepImportFile = lazyRetry(() => import("./StepImportFile"));

const ImportSmtp = ({ onFinish }) => {
  const lang = useLanguage((s) => s.language);
  const [activeStep, setActiveStep] = useState(1);

  const defaultFields = useMemo(() => {
    let result = {};

    Object.keys(system_map).forEach((key) => {
      system_map[key].forEach((value) => {
        result[value] = key;
      });
    });

    return result;
  }, []);

  const options = useMemo(() => {
    let result = [
      {
        label: `${lang.emails_account.columns.sender}`,
        value: "sender",
      },
      {
        label: `${lang.emails_account.smtp.step_base.email}`,
        value: "email",
      },
      {
        label: `${lang.emails_account.smtp.step_base.smtp_password}`,
        value: "password",
      },
      {
        label: `${lang.emails_account.smtp.step_base.smtp_host} SMTP`,
        value: "smtp_host",
      },
      {
        label: `${lang.emails_account.smtp.step_base.smtp_port} SMTP`,
        value: "smtp_port",
      },
      {
        label: `${lang.emails_account.smtp.step_base.imap_host} IMAP`,
        value: "imap_host",
      },
      {
        label: `${lang.emails_account.smtp.step_base.imap_port} IMAP`,
        value: "imap_port",
      },
      {
        label:
          lang.emails_account.smtp.step_suppl.sending_frequency_start_field,
        value: "sending_frequency_start",
      },
      {
        label: lang.emails_account.smtp.step_suppl.sending_frequency_end_field,
        value: "sending_frequency_end",
      },
    ];

    Array(14)
      .fill(true)
      .forEach((_, i) => {
        result.push({
          label:
            lang.emails_account.smtp.step_suppl.warmup_day_field +
            (i < 8 ? " 0" : " ") +
            (i + 1),
          value: "day" + (i + 1),
        });
      });

    return result;
  }, [lang]);

  const handleNext = useCallback(() => {
    setActiveStep((prev) => prev + 1);
  }, []);

  const handlePrev = () => {
    setActiveStep((prev) => prev - 1);
  };

  const [file, setFile] = useState(null);
  const [data, setData] = useState(null);
  const [fields, setFields] = useState(defaultFields);
  const [importing, setImporting] = useState(false);

  const getNextDisabled = () => {
    if (activeStep === 1) {
      return !file || data?.length === 0;
    }
    if (activeStep === 2) {
      let statFields = {};
      Object.keys(fields || {}).forEach((field) => {
        if (Object.keys(data?.[0]).includes(field)) {
          statFields = { ...statFields, [field]: fields[field] };
        }
      });
      return Object.values(statFields).filter((x) => !!x).length === 0;
    }
    return false;
  };

  const navigate = useNavigate();
  const handleImport = useCallback(
    async ({ tlsSmtp, tlsImap }) => {
      const parsedFields = {};
      Object.keys(data[0]).forEach((key) => {
        parsedFields[key] = options.find((x) => x.value === fields[key])?.value;
      });

      let emails = data.map((contact) => {
        let props = {};
        Object.keys(contact).forEach((column) => {
          if (parsedFields[column] && contact[column]) {
            props[parsedFields[column]] = contact[column];
          }
        });

        return props;
      });

      // Divide requests
      async function mainFunction() {
        let requests = [];
        const numberOfUploads = emails.length;

        const importFunction = (slicedData) => {
          let parsedData = {
            ...defaultSMTPConfigs,
            name: slicedData.sender,
            sender: slicedData.sender,
            email: slicedData.email,
            reply_to: [slicedData.email],
            smtp_username: slicedData.email,
            smtp_password: slicedData.password,
            smtp_host: slicedData.smtp_host,
            smtp_port: parseInt(slicedData.smtp_port),
            smtp_secure: tlsSmtp ? "tls" : "ssl",
            imap_username: slicedData.email,
            imap_password: slicedData.password,
            imap_host: slicedData.imap_host,
            imap_port: parseInt(slicedData.imap_port),
            imap_secure: tlsImap ? "tls" : "ssl",

            sending_frequency_start:
              parseInt(slicedData.sending_frequency_start) ||
              defaultSMTPConfigs.sending_frequency_start,
            sending_frequency_end:
              parseInt(slicedData.sending_frequency_end) ||
              defaultSMTPConfigs.sending_frequency_end,
            warm_up: {
              day_1: slicedData.day1 ? parseInt(slicedData.day1) : null,
              day_2: slicedData.day2 ? parseInt(slicedData.day2) : null,
              day_3: slicedData.day3 ? parseInt(slicedData.day3) : null,
              day_4: slicedData.day4 ? parseInt(slicedData.day4) : null,
              day_5: slicedData.day5 ? parseInt(slicedData.day5) : null,
              day_6: slicedData.day6 ? parseInt(slicedData.day6) : null,
              day_7: slicedData.day7 ? parseInt(slicedData.day7) : null,
              day_8: slicedData.day8 ? parseInt(slicedData.day8) : null,
              day_9: slicedData.day9 ? parseInt(slicedData.day9) : null,
              day_10: slicedData.day10 ? parseInt(slicedData.day10) : null,
              day_11: slicedData.day11 ? parseInt(slicedData.day11) : null,
              day_12: slicedData.day12 ? parseInt(slicedData.day12) : null,
              day_13: slicedData.day13 ? parseInt(slicedData.day13) : null,
              day_14: slicedData.day14 ? parseInt(slicedData.day14) : null,
              day_15: slicedData.day15 ? parseInt(slicedData.day15) : null,
            },
          };

          return createSMTP(parsedData);
        };

        for (let i = 0; i < numberOfUploads; i++) {
          requests.push(
            new Promise((resolve, reject) =>
              importFunction(emails[i])
                .then((res) => resolve(res))
                .catch((err) => reject(err))
            )
          );
        }

        setImporting(true);
        return Promise.allSettled(requests)
          .then((values) => {
            if (!values.find((x) => x.status === "rejected")) {
              navigate("/senders/email");
            }
          })
          .finally(() => {
            setImporting(false);
          });
      }

      return mainFunction();
    },
    [data, fields, navigate, options]
  );

  const getStepComponent = useCallback(() => {
    if (activeStep === 1) {
      return (
        <StepImportFile
          file={file}
          setFile={setFile}
          data={data}
          setData={setData}
        />
      );
    }
    if (activeStep === 2) {
      return (
        <StepMapping
          data={data}
          fields={fields}
          setFields={setFields}
          options={options}
        />
      );
    }
    if (activeStep === 3) {
      return (
        <StepImportSettings
          file={file}
          data={data}
          fields={fields}
          handlePrev={handlePrev}
          handleImport={handleImport}
          setImporting={setImporting}
          onFinish={onFinish}
        />
      );
    }
  }, [activeStep, file, data, fields, options, handleImport, onFinish]);

  return (
    <Paper
      mx={"auto"}
      mt={32}
      p={16}
      mb={32}
      gap={"xs"}
      w={"80%"}
      style={{
        boxShadow: "#00000016 0 2px 6px",
        background: "#fff",
        borderRadius: 12,
        width: "90%",
      }}
      h={"calc(100vh - 128px)"}
      component={Stack}
    >
      <CloseButton
        size={40}
        onClick={() => navigate("/senders/email/create/smtp")}
      />
      <LoadingOverlay visible={importing} overlayProps={{ blur: 1 }} />
      <Stepper active={activeStep - 1} iconSize={24} w={"95%"}>
        <Stepper.Step
          label={lang.contact_lists.import.steps_title.file}
          description={
            file?.name || lang.contact_lists.import.steps_title.file_text
          }
          styles={{
            stepDescription: {
              maxWidth: 210,
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          }}
        />
        <Stepper.Step
          label={lang.contact_lists.import.steps_title.mapping}
          description={lang.contact_lists.import.steps_title.mapping_text}
        />
        <Stepper.Step
          label={lang.contact_lists.import.steps_title.importing}
          description={lang.contact_lists.import.steps_title.importing_text}
        />
      </Stepper>

      {getStepComponent()}

      {activeStep !== 3 && (
        <Group mt={"auto"}>
          {activeStep === 2 && <MappingStats fields={fields} data={data} />}
          <Group ml={"auto"}>
            {activeStep !== 1 && (
              <Button
                variant="subtle"
                leftSection={<IconChevronLeft />}
                onClick={handlePrev}
                size="md"
              >
                {lang.global.back}
              </Button>
            )}
            <Button
              rightSection={<IconChevronRight />}
              onClick={handleNext}
              disabled={getNextDisabled()}
              size="md"
            >
              {lang.global.next}
            </Button>
          </Group>
        </Group>
      )}
    </Paper>
  );
};

export default ImportSmtp;
